/**
 * Globe data bridge — feeds the 3D globe backend from the same state the
 * flat layer system renders: store events (time-filtered, gated by
 * activeLayers), static reference datasets, and analysis results.
 *
 * The flat layer system (layers/index.ts) is torn down while the globe is
 * active, so this module owns its own subscriptions and its own
 * hotspot/dark-ship caches. Marker volume is bounded by the ported WM
 * marker budget (every globe marker is a DOM node repositioned per frame).
 */

import type { NormalizedEvent } from '@/sources/base-source';
import type { HotspotEscalation } from '@/intelligence/analysis/types';
import type { AISAnomaly } from '@/intelligence/analysis/ais-spoofing';
import type { GlobeInstance } from 'globe.gl';
import { appStore } from '@/core/store';
import { eventBus } from '@/core/events';
import { filterEventsByTimeRange } from '@/utils/time-filter';
import { getAnalysisEngine } from '@/intelligence/analysis/engine';
import { escapeHtml } from '@/utils/sanitize';
import { buildEventIndex } from './layers/index';
import { isMilitaryEvent } from './layers/military-activity-layer';
import { buildConflictZoneFeatures } from './layers/conflict-zone-layer';
import { getAllCountryFeatures, initCountryDetection, isCountryDetectionReady } from './country-detect';
import { initDarkShipCache, getDarkShips } from './dark-ship-cache';
import { getCIIClassification } from '@/intelligence/analysis/cii';
import type { GlobeMarker } from './globe-markers';
import type { GlobeArc, GlobePath } from './globe-arcs';
import type { ConflictZonePolygon, CIIPolygon, ConflictIntensity } from './globe-polygons';
import {
  extractDisplacementArcs,
  FLOW_SOURCE_COLOR,
  FLOW_TARGET_COLOR,
  type DisplacementArc,
} from './layers/displacement-layer';
import { resolveTradeRouteSegments } from '@/config/data';
import { UNDERSEA_CABLES, PIPELINES } from '@/config/data';
import { computeOrbitalTracks } from './layers/satellite-layer';
import { fetchSatellites, getCachedRecords } from '@/sources/built-in/satellites';
import type { SatellitePosition, SatRecord } from '@/sources/built-in/satellites';
import {
  selectGlobeMarkers,
  proximityRank,
  GLOBE_MARKER_BUDGET_DESKTOP,
  GLOBE_MARKER_BUDGET_MOBILE,
  type GlobeMarkerGroup,
} from './globe-marker-budget';
import { MILITARY_BASES, NUCLEAR_FACILITIES } from '@/config/data/geo';
import { PORTS } from '@/config/data/ports';
import { AI_DATA_CENTERS } from '@/config/data/ai-datacenters';
import type { MilitaryBaseType } from '@/config/data/types';

// ── Types ────────────────────────────────────────────────────────────

/** A globe marker carrying its source event for click-through selection. */
export type BridgeMarker = GlobeMarker & { _event?: NormalizedEvent };

/** The subset of MapContainer the bridge pushes data into. */
export interface GlobeDataTarget {
  setGlobeMarkers(markers: object[]): void;
  setGlobeArcs(arcs: GlobeArc[]): void;
  setGlobePaths(paths: GlobePath[]): void;
  setConflictZones(zones: ConflictZonePolygon[]): void;
  setCIIScores(countries: CIIPolygon[]): void;
  getGlobe(): GlobeInstance | null;
}

// ── State ────────────────────────────────────────────────────────────

let target: GlobeDataTarget | null = null;
let unsubscribers: (() => void)[] = [];
let debounceTimer: ReturnType<typeof setTimeout> | null = null;

/** Hotspot escalations, replaced each analysis cycle. */
let hotspotCache: HotspotEscalation[] = [];

// Satellite feed — bridge-owned and GATED on layer activity, deliberately
// unlike the flat system's unconditional 60s timer: every globe marker is
// a DOM node, so idle sessions should not pay for a feed nobody renders.
let satellitePositions: SatellitePosition[] = [];
let satelliteTimer: ReturnType<typeof setInterval> | null = null;
let satelliteFetching = false;
let satelliteGeneration = 0;

const SATELLITE_GATE_LAYERS = ['satellites', 'satellite-tracks'] as const;
const SATELLITE_REFRESH_MS = 60_000;

/** Minimal structural type for the globe's OrbitControls event API. */
interface ControlsEvents {
  addEventListener(type: string, handler: () => void): void;
  removeEventListener(type: string, handler: () => void): void;
}
let cameraControls: ControlsEvents | null = null;
let cameraEndHandler: (() => void) | null = null;

const REBUILD_DEBOUNCE_MS = 150;

// ── Value helpers ────────────────────────────────────────────────────

function num(value: unknown, fallback = 0): number {
  const n = typeof value === 'string' ? Number(value) : value;
  return typeof n === 'number' && Number.isFinite(n) ? n : fallback;
}

function str(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

type EventSeverity = NormalizedEvent['severity'];

function severityToWeather(s: EventSeverity): 'minor' | 'moderate' | 'severe' | 'extreme' {
  if (s === 'critical') return 'extreme';
  if (s === 'high') return 'severe';
  if (s === 'medium') return 'moderate';
  return 'minor';
}

function severityToCyber(s: EventSeverity): 'low' | 'medium' | 'high' | 'critical' {
  if (s === 'critical' || s === 'high' || s === 'medium') return s;
  return 'low';
}

function severityTo5(s: EventSeverity): 1 | 2 | 3 | 4 | 5 {
  switch (s) {
    case 'critical': return 5;
    case 'high': return 4;
    case 'medium': return 3;
    case 'low': return 2;
    default: return 1;
  }
}

// ── Event -> marker mapping ──────────────────────────────────────────

/**
 * Map one event to a globe marker for the given layer. Returns null for
 * layers the globe does not render as HTML markers (choropleths, arcs,
 * density grids) and for events without a usable location.
 */
export function eventToMarker(layerId: string, e: NormalizedEvent): BridgeMarker | null {
  if (!e.location) return null;
  const { lat, lng } = e.location;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  const raw = (e.raw ?? {}) as Record<string, unknown>;

  switch (layerId) {
    case 'conflicts':
      return {
        _kind: 'conflict', lat, lng, name: e.title,
        fatalities: num(raw.fatalities), type: str(raw.event_type, e.type), _event: e,
      };
    case 'protests':
      return {
        _kind: 'protest', lat, lng, title: e.title,
        size: num(raw.size ?? raw.crowd_size), _event: e,
      };
    case 'earthquakes':
      return {
        _kind: 'earthquake', lat, lng,
        magnitude: num(raw.magnitude ?? raw.mag),
        depth: num(raw.depth),
        location: e.location.name ?? e.title, _event: e,
      };
    case 'fires':
      return {
        _kind: 'fire', lat, lng,
        brightness: num(raw.brightness ?? raw.bright_ti4, 300),
        confidence: num(raw.confidence),
        frp: num(raw.frp), _event: e,
      };
    case 'weather-alerts':
      return {
        _kind: 'weather', lat, lng,
        type: str(raw.event, e.title),
        severity: severityToWeather(e.severity), _event: e,
      };
    case 'cyber-threats':
      return {
        _kind: 'cyber', lat, lng,
        target: e.location.country ?? e.title,
        attackType: str(raw.attackType, e.type),
        severity: severityToCyber(e.severity), _event: e,
      };
    case 'news-markers':
      return {
        _kind: 'news', lat, lng, headline: e.title,
        source: e.source, category: e.tags?.[0] ?? '', _event: e,
      };
    case 'displacement':
      return {
        _kind: 'displacement', lat, lng,
        population: num(raw.population ?? raw.total),
        origin: str(raw.origin, e.location.name ?? ''),
        cause: str(raw.cause), _event: e,
      };
    case 'gps-jamming':
      return {
        _kind: 'gpsjam', lat, lng,
        severity: severityTo5(e.severity), source: e.source, _event: e,
      };
    case 'vessels':
    case 'naval':
      return {
        _kind: 'vessel', lat, lng, name: e.title,
        mmsi: String(raw.mmsi ?? ''),
        // vessel-source emits the flag code as raw.flag
        country: str(raw.flag ?? raw.country, e.location.countryCode ?? ''),
        type: str(raw.shipTypeLabel ?? raw.type), _event: e,
      };
    case 'flights':
    case 'military-aircraft':
      return {
        _kind: 'flight', lat, lng, callsign: e.title,
        // buildFlight labels the value as feet; opensky emits raw.altitudeFt
        // and location.altitude is geoAltitude in METERS — convert.
        altitude: raw.altitudeFt != null
          ? num(raw.altitudeFt)
          : num(e.location.altitude) * 3.28084,
        country: str(raw.country),
        heading: num(raw.heading ?? raw.track), _event: e,
      };
    case 'radiation-watch':
      return {
        _kind: 'radiation', lat, lng,
        level: num(raw.value), unit: str(raw.unit, 'uSv/h'),
        station: e.title, _event: e,
      };
    default:
      return null;
  }
}

/** Event-driven layers the globe renders, in WM's flush order. */
const EVENT_MARKER_LAYERS = [
  'conflicts', 'protests', 'earthquakes', 'fires', 'weather-alerts',
  'cyber-threats', 'news-markers', 'displacement', 'gps-jamming',
  'vessels', 'naval', 'flights', 'military-aircraft', 'radiation-watch',
] as const;

/** Per-layer severity rank (WM parity); layers absent here rank by proximity. */
const LAYER_RANK: Partial<Record<string, (m: BridgeMarker) => number>> = {
  conflicts: (m) => (m._kind === 'conflict' ? m.fatalities : 0),
  earthquakes: (m) => (m._kind === 'earthquake' ? m.magnitude : 0),
  fires: (m) => (m._kind === 'fire' ? m.brightness : 0),
};

// ── Static reference markers ─────────────────────────────────────────

const BASE_ALLIANCE: Record<MilitaryBaseType, 'us-nato' | 'russia' | 'china' | 'india' | 'other'> = {
  'us-nato': 'us-nato',
  uk: 'us-nato',
  france: 'us-nato',
  italy: 'us-nato',
  turkey: 'us-nato',
  japan: 'us-nato',
  russia: 'russia',
  china: 'china',
  india: 'india',
  uae: 'other',
  other: 'other',
};

function nuclearStatus(status: string): 'operational' | 'decommissioned' | 'under-construction' {
  if (status === 'active' || status === 'contested') return 'operational';
  if (status === 'construction') return 'under-construction';
  return 'decommissioned';
}

/**
 * Synthetic event for a static reference marker, mirroring the flat
 * map's staticToEvent so a click opens the detail panel in both modes.
 * The RAW dataset id is kept as the event id — 2D's staticToEvent uses
 * it too, so per-object panel state (verification checklist, AI-summary
 * cache) is shared across mode switches.
 */
function staticEvent(
  id: string,
  name: string,
  type: NormalizedEvent['type'],
  lat: number,
  lng: number,
  description?: string,
): NormalizedEvent {
  return {
    id,
    title: name,
    source: 'static',
    timestamp: new Date().toISOString(),
    type,
    severity: 'info',
    description,
    location: { lat, lng },
  };
}

/** Build static reference marker groups for the active layers. */
export function buildStaticMarkerGroups(activeLayers: Set<string>): GlobeMarkerGroup<BridgeMarker>[] {
  const groups: GlobeMarkerGroup<BridgeMarker>[] = [];

  if (activeLayers.has('military-bases')) {
    groups.push({
      layer: 'military-bases',
      markers: MILITARY_BASES.map((b): BridgeMarker => ({
        _kind: 'milbase', lat: b.lat, lng: b.lon, name: b.name,
        alliance: BASE_ALLIANCE[b.type] ?? 'other',
        _event: staticEvent(b.id, b.name, 'military', b.lat, b.lon,
          b.description ?? [b.country, b.arm].filter(Boolean).join(' — ')),
      })),
    });
  }
  if (activeLayers.has('nuclear')) {
    groups.push({
      layer: 'nuclear',
      markers: NUCLEAR_FACILITIES.map((f): BridgeMarker => ({
        _kind: 'nuclear', lat: f.lat, lng: f.lon, name: f.name,
        status: nuclearStatus(f.status),
        _event: staticEvent(f.id, f.name, 'infrastructure', f.lat, f.lon,
          [f.type, f.status, f.operator].filter(Boolean).join(' — ')),
      })),
    });
  }
  if (activeLayers.has('ports')) {
    groups.push({
      layer: 'ports',
      markers: PORTS.map((p): BridgeMarker => ({
        _kind: 'port', lat: p.lat, lng: p.lon, name: p.name,
        country: p.country, vessels: 0,
        _event: staticEvent(p.id, p.name, 'infrastructure', p.lat, p.lon,
          [p.country, p.note].filter(Boolean).join(' — ')),
      })),
    });
  }
  if (activeLayers.has('datacenters')) {
    groups.push({
      layer: 'datacenters',
      markers: AI_DATA_CENTERS
        .filter((dc) => dc.status !== 'decommissioned')
        .map((dc): BridgeMarker => ({
          _kind: 'datacenter', lat: dc.lat, lng: dc.lon, name: dc.name,
          operator: dc.owner,
          _event: staticEvent(dc.id, dc.name, 'ai_infrastructure', dc.lat, dc.lon,
            [dc.owner, dc.country].filter(Boolean).join(' — ')),
        })),
    });
  }

  return groups;
}

// ── Marker group assembly ────────────────────────────────────────────

/**
 * Build all marker groups from events + analysis caches, gated by
 * activeLayers. Pure — exported for tests.
 */
export function buildMarkerGroups(
  events: NormalizedEvent[],
  activeLayers: Set<string>,
  hotspots: HotspotEscalation[],
  darkShips: AISAnomaly[],
  satellites: SatellitePosition[] = [],
): GlobeMarkerGroup<BridgeMarker>[] {
  const index = buildEventIndex(events);
  const groups: GlobeMarkerGroup<BridgeMarker>[] = [];

  // Analysis-driven layers first (highest signal density)
  if (activeLayers.has('hotspots') && hotspots.length > 0) {
    groups.push({
      layer: 'hotspots',
      markers: hotspots.map((h): BridgeMarker => ({
        _kind: 'hotspot', lat: h.lat, lng: h.lng, label: h.hotspotName,
        severity: Math.min(5, Math.max(1, Math.ceil(h.score / 20))) as 1 | 2 | 3 | 4 | 5,
      })),
      rank: (m) => (m._kind === 'hotspot' ? m.severity : 0),
    });
  }
  if (activeLayers.has('dark-ships') && darkShips.length > 0) {
    groups.push({
      layer: 'dark-ships',
      markers: darkShips.map((a): BridgeMarker => ({
        _kind: 'darkship', lat: a.lat, lng: a.lng, name: a.vesselName,
        mmsi: String(a.mmsi), lastSeen: a.detectedAt, country: '',
      })),
    });
  }

  // Event-driven layers. When both the "all" and "military-only" variants
  // of a feed are active, render only the superset to avoid duplicates.
  const skip = new Set<string>();
  if (activeLayers.has('vessels')) skip.add('naval');
  if (activeLayers.has('flights')) skip.add('military-aircraft');

  for (const layerId of EVENT_MARKER_LAYERS) {
    if (!activeLayers.has(layerId) || skip.has(layerId)) continue;

    let layerEvents = index.get(layerId) ?? [];
    if (layerId === 'naval' || layerId === 'military-aircraft') {
      layerEvents = layerEvents.filter(isMilitaryEvent);
    }
    if (layerEvents.length === 0) continue;

    const markers: BridgeMarker[] = [];
    for (const e of layerEvents) {
      const marker = eventToMarker(layerId, e);
      if (marker) markers.push(marker);
    }
    if (markers.length === 0) continue;

    const group: GlobeMarkerGroup<BridgeMarker> = { layer: layerId, markers };
    const rank = LAYER_RANK[layerId];
    if (rank) group.rank = rank;
    groups.push(group);
  }

  groups.push(...buildStaticMarkerGroups(activeLayers));

  // Satellites last (WM flush order); no intrinsic rank — proximity only.
  if (activeLayers.has('satellites') && satellites.length > 0) {
    groups.push({
      layer: 'satellites',
      markers: satellites.map((s): BridgeMarker => ({
        _kind: 'satellite', lat: s.lat, lng: s.lng, name: s.name,
        noradId: Number(s.id) || 0, country: s.country, alt: s.alt,
      })),
    });
  }

  return groups;
}

// ── Arc / path builders ──────────────────────────────────────────────

/** Flat-map cap: top displacement flows by scale (WM DeckGLMap.ts:6472). */
const MAX_DISPLACEMENT_ARCS = 50;

/**
 * The visible displacement flow set: the displacement layer's own events
 * (the coord-pair fields are not exclusive to displacement — arc-layer.ts
 * reserves the same pattern for shipping lanes), top-50 by scale like the
 * flat map. Pure — exported for tests.
 */
export function selectDisplacementFlows(events: NormalizedEvent[]): DisplacementArc[] {
  const layerEvents = buildEventIndex(events).get('displacement') ?? [];
  return extractDisplacementArcs(layerEvents)
    .sort((a, b) => b.scale - a.scale)
    .slice(0, MAX_DISPLACEMENT_ARCS);
}

/**
 * Content key for the visible flow set — event ids plus endpoints, so the
 * arc push tracks what is actually rendered rather than a generation
 * counter. Pure — exported for tests.
 */
export function displacementFlowsKey(flows: DisplacementArc[]): string {
  return flows
    .map((f) => `${f.event.id}@${f.source[0]},${f.source[1]}>${f.target[0]},${f.target[1]}`)
    .join(';');
}

function rgbaString([r, g, b, a]: [number, number, number, number]): string {
  return `rgba(${r},${g},${b},${(a / 255).toFixed(2)})`;
}

/** Flat map's displacement gradient as globe.gl rgba color stops. */
const DISPLACEMENT_GRADIENT = [rgbaString(FLOW_SOURCE_COLOR), rgbaString(FLOW_TARGET_COLOR)];

/**
 * Build globe arcs for the active layers: trade-route segments (static)
 * and displacement flows (event-driven, top-50 by scale like the flat
 * map). All source positions are [lon, lat] pairs. Pure — exported for
 * tests.
 */
export function buildGlobeArcs(events: NormalizedEvent[], activeLayers: Set<string>): GlobeArc[] {
  const arcs: GlobeArc[] = [];

  if (activeLayers.has('trade-routes')) {
    for (const seg of resolveTradeRouteSegments()) {
      arcs.push({
        startLat: seg.sourcePosition[1],
        startLng: seg.sourcePosition[0],
        endLat: seg.targetPosition[1],
        endLng: seg.targetPosition[0],
        status: seg.status,
        category: seg.category,
        label: `${seg.routeName} — ${seg.volumeDesc}`,
      });
    }
  }

  if (activeLayers.has('displacement')) {
    for (const flow of selectDisplacementFlows(events)) {
      arcs.push({
        startLat: flow.source[1],
        startLng: flow.source[0],
        endLat: flow.target[1],
        endLng: flow.target[0],
        color: [...DISPLACEMENT_GRADIENT],
        // globe.gl tooltips render labels via innerHTML; titles carry
        // external feed data, so escape (flat mode uses textContent).
        label: escapeHtml(flow.event.title),
      });
    }
  }

  return arcs;
}

/** Layer ids that feed globe paths, in render order. */
const GLOBE_PATH_LAYERS = ['undersea-cables', 'pipelines-oil', 'pipelines-gas'] as const;

function toPathPoints(points: [number, number][]): GlobePath['points'] {
  return points.map(([lng, lat]) => ({ lat, lng }));
}

/**
 * Build globe paths for the active layers from the same static datasets
 * the flat PathLayers render. Cable health status is not joined here —
 * the flat cable layer renders a static color too. Pure — exported for
 * tests.
 */
export function buildGlobePaths(activeLayers: Set<string>): GlobePath[] {
  const paths: GlobePath[] = [];

  if (activeLayers.has('undersea-cables')) {
    for (const cable of UNDERSEA_CABLES) {
      paths.push({ points: toPathPoints(cable.points), pathType: 'cable' });
    }
  }
  if (activeLayers.has('pipelines-oil')) {
    for (const p of PIPELINES.filter((p) => p.type === 'oil')) {
      paths.push({ points: toPathPoints(p.points), pathType: 'pipeline-oil' });
    }
  }
  if (activeLayers.has('pipelines-gas')) {
    for (const p of PIPELINES.filter((p) => p.type === 'gas')) {
      paths.push({ points: toPathPoints(p.points), pathType: 'pipeline-gas' });
    }
  }

  return paths;
}

/**
 * Track computation cap — matches the flat layer's global-view limit
 * (satellite-layer.ts: 200 below zoom 5; the globe is a global view).
 */
const ORBIT_TRACK_LIMIT = 200;

/**
 * Orbit trails as GlobePaths when satellite-tracks is active. Altitude is
 * scaled to globe-radius fractions (km / 6371); the record country feeds
 * getPathColor's ORBIT_COLORS via status. Pure — exported for tests.
 */
export function buildOrbitPaths(records: SatRecord[] | null, activeLayers: Set<string>): GlobePath[] {
  if (!activeLayers.has('satellite-tracks') || !records || records.length === 0) return [];

  return computeOrbitalTracks(records, ORBIT_TRACK_LIMIT, 1 / 6371).map((track) => ({
    points: track.path.map(([lng, lat, alt]) => ({ lat, lng, alt })),
    pathType: 'orbit' as const,
    status: track.country,
    // The source's full country palette — ORBIT_COLORS only knows
    // CN/RU/US/EU and would misattribute e.g. KR trails as US.
    color: rgbaString(track.color),
  }));
}

// ── Satellite feed ───────────────────────────────────────────────────

function refreshSatellites(): void {
  if (satelliteFetching) return;
  satelliteFetching = true;
  fetchSatellites()
    .then((positions) => {
      // Feed switched off (or bridge destroyed) while in flight — discard.
      if (satelliteTimer === null) return;
      satellitePositions = positions;
      satelliteGeneration++;
      scheduleDataRebuild();
    })
    .catch(() => {
      // Keep stale positions — next tick retries.
    })
    .finally(() => {
      satelliteFetching = false;
    });
}

/**
 * Start or stop the 60s satellite feed based on layer activity.
 * Exported for tests.
 */
export function syncSatelliteFeed(activeLayers: Set<string>): void {
  const wanted = SATELLITE_GATE_LAYERS.some((id) => activeLayers.has(id));
  if (wanted && satelliteTimer === null) {
    satelliteTimer = setInterval(refreshSatellites, SATELLITE_REFRESH_MS);
    refreshSatellites();
  } else if (!wanted && satelliteTimer !== null) {
    clearInterval(satelliteTimer);
    satelliteTimer = null;
    if (satellitePositions.length > 0) {
      satellitePositions = [];
      satelliteGeneration++;
    }
  }
}

// ── Polygon builders ─────────────────────────────────────────────────

/** Conflict zone polygons from the same features the flat layer renders. */
export function buildConflictZonePolygons(): ConflictZonePolygon[] {
  const collection = buildConflictZoneFeatures();
  const zones: ConflictZonePolygon[] = [];
  for (const feature of collection.features) {
    const geometry = feature.geometry;
    if (!geometry || (geometry.type !== 'Polygon' && geometry.type !== 'MultiPolygon')) continue;
    const props = (feature.properties ?? {}) as { name?: string; intensity?: string };
    const intensity: ConflictIntensity =
      props.intensity === 'medium' || props.intensity === 'low' ? props.intensity : 'high';
    zones.push({ geometry, intensity, name: props.name ?? '' });
  }
  return zones;
}

/** CII choropleth polygons from the analysis engine's latest scores. */
export function buildCIIPolygons(): CIIPolygon[] {
  const scores = getAnalysisEngine()?.getLastResult()?.ciiScores;
  if (!scores || scores.size === 0) return [];

  const polygons: CIIPolygon[] = [];
  for (const feature of getAllCountryFeatures()) {
    const props = (feature as { properties?: { code?: string } }).properties;
    const code = props?.code?.toUpperCase();
    if (!code) continue;
    const score = scores.get(code);
    if (!score) continue;
    const geometry = (feature as { geometry?: GeoJSON.Geometry }).geometry;
    if (!geometry || (geometry.type !== 'Polygon' && geometry.type !== 'MultiPolygon')) continue;
    polygons.push({
      geometry,
      countryCode: code,
      level: getCIIClassification(score.overall),
      score: score.overall,
    });
  }
  return polygons;
}

// ── Rebuild ──────────────────────────────────────────────────────────

function isMobileDevice(): boolean {
  return typeof window !== 'undefined'
    && ('ontouchstart' in window || navigator.maxTouchPoints > 0);
}

// Marker groups are cached across camera-only reselects: three-globe
// binds meshes/DOM nodes to datum object IDENTITY, so rebuilding marker
// objects on every camera settle would recreate every CSS2D node. Only
// data changes (generation bump) rebuild the group objects; camera
// settles merely re-run the budget selection over the cached groups.
let dataGeneration = 0;
let builtGeneration = -1;
let cachedGroups: GlobeMarkerGroup<BridgeMarker>[] = [];

/** Analysis cycle counter — polygon memo key for the CII choropleth. */
let analysisGeneration = 0;

// Polygons are memoized the same way: pushing them re-merges into new
// tagged objects, which re-tessellates every country mesh. Only push
// when the inputs actually changed.
let lastZonesKey: string | null = null;
let lastCIIKey: string | null = null;

// Arcs and paths are memoized the same way. Re-pushing arcs restarts
// their dash-animation phase (visible blink), so the key must NOT churn
// on feed ticks unless an event-driven arc source is actually active.
let lastArcsKey: string | null = null;
let lastPathsKey: string | null = null;

/**
 * Memo key for the arc push. Only displacement arcs depend on data;
 * trade routes are static, so the displacement CONTENT key (the visible
 * flow set, not a generation counter — sliding time windows age events
 * out with the wall clock and no counter bumps for that) enters only
 * while displacement is active. Pure — exported for tests.
 */
export function arcsMemoKey(activeLayers: Set<string>, displacementKey: string): string {
  const tradeActive = activeLayers.has('trade-routes');
  const dispActive = activeLayers.has('displacement');
  return `${tradeActive}:${dispActive}:${dispActive ? displacementKey : ''}`;
}

function rebuildMarkers(): void {
  if (!target) return;

  const state = appStore.getState();
  const activeLayers = new Set(state.activeLayers);

  if (builtGeneration !== dataGeneration) {
    const events = filterEventsByTimeRange(state.events, state.timeRange, state.timeCursor);
    cachedGroups = buildMarkerGroups(events, activeLayers, hotspotCache, getDarkShips(), satellitePositions);
    builtGeneration = dataGeneration;
  }

  // Rank unranked layers by nearness to the camera focus; ranked layers
  // use nearness as the tie-break (WM flush parity). Shallow copies —
  // the cached groups keep their intrinsic severity ranks untouched.
  const pov = target.getGlobe()?.pointOfView();
  const nearestFirst = proximityRank<BridgeMarker>(
    { lat: pov?.lat ?? 0, lng: pov?.lng ?? 0 },
    (m) => ({ lat: m.lat, lng: m.lng }),
  );
  const rankedGroups = cachedGroups.map((group) => group.rank
    ? { ...group, tieBreak: nearestFirst }
    : { ...group, rank: nearestFirst });

  const budget = isMobileDevice() ? GLOBE_MARKER_BUDGET_MOBILE : GLOBE_MARKER_BUDGET_DESKTOP;
  const { markers } = selectGlobeMarkers(rankedGroups, budget);
  target.setGlobeMarkers(markers);
}

function rebuildPolygons(): void {
  if (!target) return;

  const activeLayers = new Set(appStore.getState().activeLayers);
  const countriesReady = isCountryDetectionReady();

  const zonesActive = activeLayers.has('conflict-zones');
  const zonesKey = `${zonesActive}:${countriesReady}`;
  if (zonesKey !== lastZonesKey) {
    lastZonesKey = zonesKey;
    target.setConflictZones(zonesActive ? buildConflictZonePolygons() : []);
  }

  const ciiActive = activeLayers.has('cii-choropleth');
  const ciiKey = `${ciiActive}:${countriesReady}:${analysisGeneration}`;
  if (ciiKey !== lastCIIKey) {
    lastCIIKey = ciiKey;
    target.setCIIScores(ciiActive ? buildCIIPolygons() : []);
  }
}

function rebuildArcsPaths(): void {
  if (!target) return;

  const state = appStore.getState();
  const activeLayers = new Set(state.activeLayers);

  // The content key is recomputed on every full rebuild (analysis ticks
  // run one at least every 60s), so arcs age out of sliding time windows
  // without a dedicated timer — and re-push ONLY when the visible flow
  // set actually changed, never on unrelated generation bumps.
  const dispActive = activeLayers.has('displacement');
  const events = dispActive
    ? filterEventsByTimeRange(state.events, state.timeRange, state.timeCursor)
    : [];
  const arcsKey = arcsMemoKey(
    activeLayers,
    dispActive ? displacementFlowsKey(selectDisplacementFlows(events)) : '',
  );
  if (arcsKey !== lastArcsKey) {
    lastArcsKey = arcsKey;
    target.setGlobeArcs(buildGlobeArcs(events, activeLayers));
  }

  // Orbit trails are the only data-driven path source: the satellite
  // generation enters the key only while the tracks layer is active, so
  // satellite refreshes never re-push the static cable/pipeline paths
  // while trails are off.
  const tracksActive = activeLayers.has('satellite-tracks');
  const pathsKey = [
    ...GLOBE_PATH_LAYERS.map((id) => activeLayers.has(id)),
    tracksActive,
    tracksActive ? satelliteGeneration : '',
  ].join(':');
  if (pathsKey !== lastPathsKey) {
    lastPathsKey = pathsKey;
    const paths = buildGlobePaths(activeLayers);
    paths.push(...buildOrbitPaths(getCachedRecords(), activeLayers));
    target.setGlobePaths(paths);
  }
}

function rebuild(): void {
  rebuildMarkers();
  rebuildPolygons();
  rebuildArcsPaths();
}

let pendingFull = false;

/**
 * Debounced rebuild. Data changes request a full rebuild; camera settles
 * request a markers-only reselect. A full request queued in the same
 * debounce window wins.
 */
function scheduleRebuild(full = true): void {
  pendingFull = pendingFull || full;
  if (debounceTimer !== null) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    const doFull = pendingFull;
    pendingFull = false;
    if (doFull) rebuild();
    else rebuildMarkers();
  }, REBUILD_DEBOUNCE_MS);
}

/** Data changed — invalidate the marker-group cache and rebuild fully. */
function scheduleDataRebuild(): void {
  dataGeneration++;
  scheduleRebuild(true);
}


// ── Lifecycle ────────────────────────────────────────────────────────

/**
 * Start feeding the globe. Called from MapContainer when the globe3d
 * backend initializes; performs one synchronous build so the globe is
 * populated before any rehydrate replay.
 */
export function initGlobeBridge(dataTarget: GlobeDataTarget): void {
  destroyGlobeBridge();
  target = dataTarget;

  // Shared dark-ship cache — idempotent; covers sessions that boot
  // straight into globe mode without ever running the flat layer system.
  initDarkShipCache();

  // Country geometry loads async and is otherwise only triggered by the
  // deckgl path — without it the CII choropleth and the ISO-mapped
  // conflict zones have no polygons on a globe-boot session. Idempotent.
  void initCountryDetection().then(() => scheduleDataRebuild());

  // Seed analysis caches from the engine's last completed cycle
  const last = getAnalysisEngine()?.getLastResult();
  hotspotCache = last?.hotspotEscalations ?? [];

  syncSatelliteFeed(new Set(appStore.getState().activeLayers));

  unsubscribers.push(
    appStore.subscribe((s) => s.events, () => scheduleDataRebuild()),
    appStore.subscribe((s) => s.activeLayers, (layers) => {
      syncSatelliteFeed(new Set(layers));
      scheduleDataRebuild();
    }),
    appStore.subscribe((s) => s.timeRange, () => scheduleDataRebuild()),
    appStore.subscribe((s) => s.timeCursor, () => scheduleDataRebuild()),
    eventBus.on('analysis:complete', (result) => {
      hotspotCache = result.hotspotEscalations;
      analysisGeneration++;
      scheduleDataRebuild();
    }),
    eventBus.on('analysis:ais-anomaly', () => scheduleDataRebuild()),
  );

  // The proximity-ranked marker subset is only correct for the camera it
  // was computed at — re-select when the camera settles so rotating or
  // zooming brings budget-trimmed markers into view (WM #5368 behavior).
  // Markers only: polygons are camera-independent.
  const controls = dataTarget.getGlobe()?.controls() as ControlsEvents | null | undefined;
  if (controls?.addEventListener) {
    cameraControls = controls;
    cameraEndHandler = () => scheduleRebuild(false);
    controls.addEventListener('end', cameraEndHandler);
  }

  dataGeneration++;
  rebuild();
}

/** Stop feeding the globe and clear bridge state. */
export function destroyGlobeBridge(): void {
  for (const unsub of unsubscribers) unsub();
  unsubscribers = [];
  if (cameraControls && cameraEndHandler) {
    cameraControls.removeEventListener('end', cameraEndHandler);
  }
  cameraControls = null;
  cameraEndHandler = null;
  if (debounceTimer !== null) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
  pendingFull = false;
  cachedGroups = [];
  builtGeneration = -1;
  lastZonesKey = null;
  lastCIIKey = null;
  lastArcsKey = null;
  lastPathsKey = null;
  hotspotCache = [];
  if (satelliteTimer !== null) {
    clearInterval(satelliteTimer);
    satelliteTimer = null;
  }
  satellitePositions = [];
  target = null;
}
